import * as ents from '@attain-apps/entities'
import { Word } from '@attain-apps/string-builder'
import { StringCase, transformCase } from '@attain-apps/string-utils'
import { TypeChecker } from '@attain-apps/type-guards'

import * as widget from './widget'

// In GridStack, by default each row is made up of 12 square-shaped cells, with
// each cell having a span of 1.
const MAX_ROW_SPAN = 12

// The segments panel appears next to the grid when there is more than one
// segment. Once visible, it takes up 1/8 of the total width, which equals
// 12.5%.
const SEGMENT_ASIDE_WIDTH_PERCENTAGE = 12.5

export interface Segment
  extends Omit<ents.core.mosaic.Page.Segment, 'root'>,
    ents.core.common.Identified<Segment.Id> {
  widgetFrames: readonly widget.Frame[]
  // TODO Consider creating common.Indexed interface
  index: number
}

export namespace Segment {
  export type Id = Word
  export const DEFAULT_NAME = 'New Segment'

  /**
   * Converts a segment name into a clean, URL-safe alias.
   *
   * This function removes trailing numeric groupings like "(1)", strips
   * unnecessary spaces, and converts the name into kebab-case.
   *
   * @example
   * generateAliasFromName('Segment Name')         // 'segment-name'
   * generateAliasFromName('Segment (1)')          // 'segment1'
   * generateAliasFromName('Custom Segment (12)')  // 'custom-segment12'
   */
  export function generateAliasFromName(segmentName: string): string {
    const normalized = segmentName
      // Replace a trailing pattern like "(1)" with just the number, e.g.,
      // "Segment (1)" → "Segment1"
      .replace(/\s*\((\d+)\)$/, '$1')
      // Remove any single space character
      .replace(/\s+/g, '')

    // Convert to kebab-case (e.g., "SegmentName" → "segment-name")
    return transformCase(normalized, StringCase.Kebab)
  }

  /**
   * Returns a default {@link Segment} with at least one empty widget frame.
   */
  export function generate(withDefault?: Partial<Segment>): Segment {
    return {
      alias: generateAliasFromName(DEFAULT_NAME),
      id: Word.generate(),
      index: 0,
      name: DEFAULT_NAME,
      // Setting `autoPosition: true` allows the frame to be placed
      // automatically according to layout rules, which simplifies
      // initialization and avoids layout conflicts.
      widgetFrames: [widget.Frame.generate({ autoPosition: true })],
      ...withDefault,
    }
  }

  /**
   * Lookup of {@link Segment} by Segment alias
   */
  export type Lookup = Partial<Record<string, Segment>>

  export namespace Lookup {
    /**
     * Builds a {@link Lookup} and {@link Frame.Lookup} from the given
     * {@link ents.core.mosaic.Page.Content}.
     *
     * @example
     * // Input:
     * {
     *   root: {
     *     elements: [
     *       {
     *         height: 496,
     *         content: {
     *           row: {
     *             elements: [
     *               {
     *                 span: 5,
     *                 content: { widget: { widget: { alias: "foo" } } }
     *               },
     *               {
     *                 span: 7,
     *                 content: {
     *                   column: {
     *                     elements: [
     *                       {
     *                         height: 248,
     *                         content: { widget: { widget: { alias: "bar" } } }
     *                       },
     *                       {
     *                         height: 248,
     *                         content: { widget: { widget: { alias: "foo" } } }
     *                       }
     *                     ]
     *                   }
     *                 }
     *               }
     *             ]
     *           }
     *         }
     *       }
     *     ]
     *   }
     * }
     *
     * // Output:
     * {
     *   segmentLookup: {
     *     newSegment1: {
     *       alias: "newSegment1",
     *       index: 0,
     *       name: "New Segment 1",
     *       widgetFrames: [
     *         { h: 4, id: "A", w: 5, x: 0, y: 0, ... },
     *         { h: 2, id: "B", w: 7, x: 5, y: 0, ... },
     *         { h: 2, id: "C", w: 7, x: 5, y: 2, ... }
     *       ]
     *     }
     *   },
     *   widgetFrameLookup: {
     *     A: { ... },
     *     B: { ... },
     *     C: { ... }
     *   }
     * }
     */
    export function fromContent(
      content: ents.core.mosaic.Page.Content,
      gridStackCellHeight: number,
    ): {
      segmentLookup: Lookup
      widgetFrameLookup: widget.Frame.Lookup
    } {
      const segmentLookup: Segment.Lookup = {}
      const widgetFrameLookup: widget.Frame.Lookup = {}

      // Initialize layout
      const wfLayout = {
        w: MAX_ROW_SPAN, // Full width
        x: 0, // Starts at the leftmost horizontal grid position.
        y: 0, // Starts at the top vertical grid position.
      }

      /**
       * Build {@link Lookup} from
       * {@link ents.core.mosaic.Page.Content.WithoutSegments}
       */
      if (content.root?.elements) {
        const newSegment = Segment.generate({
          widgetFrames: widget.Frame.fromColumnElements(
            content.root.elements,
            gridStackCellHeight,
            wfLayout,
            widgetFrameLookup,
          ),
        })
        segmentLookup[newSegment.id] = newSegment

        /**
         * Build {@link Lookup} from
         * {@link ents.core.mosaic.Page.Content.WithSegments}
         */
      } else if (content.segments) {
        content.segments.forEach((segment, index) => {
          // At the time this method is called, GridStack occupies the full
          // container width. However, in the actual layout, segments occupy 1/8
          // (flex: 1) of the width, and GridStack occupies the remaining 7/8
          // (flex: 7). Since each GridStack cell is square-shaped, we need to
          // proportionally reduce the cell height by approximately 12.5% to
          // reflect the correct aspect ratio.
          const decreasedCellHeight =
            gridStackCellHeight * (1 - SEGMENT_ASIDE_WIDTH_PERCENTAGE / 100)
          const newSegment = Segment.generate({
            ...segment,
            index,
            widgetFrames: widget.Frame.fromColumnElements(
              segment.root.elements,
              decreasedCellHeight,
              wfLayout,
              widgetFrameLookup,
            ),
          })
          segmentLookup[newSegment.id] = newSegment
        })
      }
      return { segmentLookup, widgetFrameLookup }
    }

    /**
     * Converts a {@link Lookup} object into a
     * {@link ents.core.mosaic.Page.Content} structure.
     *
     * @example
     * // Input:
     * {
     *   alias: "newSegment1",
     *   index: 1,
     *   name: "New Segment 1",
     *   widgetFrames: [
     *     { h: 4, id: "A", w: 5, x: 0, y: 0, ... },
     *     { h: 2, id: "B", w: 7, x: 5, y: 0, ... },
     *     { h: 2, id: "C", w: 7, x: 5, y: 2, ... }
     *   ]
     * }
     *
     * // Output:
     * {
     *   root: {
     *     elements: [
     *       {
     *         height: 496,
     *         content: {
     *           row: {
     *             elements: [
     *               {
     *                 span: 5,
     *                 content: { widget: { widget: { alias: "foo" } } }
     *               },
     *               {
     *                 span: 7,
     *                 content: {
     *                   column: {
     *                     elements: [
     *                       {
     *                         height: 248,
     *                         content: { widget: { widget: { alias: "bar" } } }
     *                       },
     *                       {
     *                         height: 248,
     *                         content: { widget: { widget: { alias: "foo" } } }
     *                       }
     *                     ]
     *                   }
     *                 }
     *               }
     *             ]
     *           }
     *         }
     *       }
     *     ]
     *   }
     * }
     *
     * - Widget A  -> x:0, y:0, w:5, h:4
     * - Widget B  -> x:5, y:0, w:7, h:2
     * - Widget C  -> x:5, y:2, w:7, h:2
     *
     * // Grid visualization :
     * Row 0:  A A A A A | B B B B B B B
     * Row 1:  A A A A A | B B B B B B B
     * Row 2:  A A A A A | C C C C C C C
     * Row 3:  A A A A A | C C C C C C C
     */
    export function toContent(
      segmentLookup: Lookup,
      gridStackCellHeight: number,
      widgetFrameLookup: widget.Frame.Lookup,
    ): ents.core.mosaic.Page.Content {
      const segments = Object.values(segmentLookup)
        .filter(TypeChecker.isNotNullish.raw)
        .sort((a, b) => a.index - b.index)

      if (segments.length === 0) {
        throw new Error('No segments found when building content')
      }

      if (segments.length === 1) {
        return _buildWithoutSegmentsContent(
          segments[0],
          gridStackCellHeight,
          widgetFrameLookup,
        )
      } else {
        return _buildWithSegmentsContent(
          segments,
          gridStackCellHeight,
          widgetFrameLookup,
        )
      }
    }
  }
}

/**
 * Converts {@link Segment} into
 * {@link ents.core.mosaic.Page.Content.WithoutSegments}
 */
function _buildWithoutSegmentsContent(
  segment: Segment,
  gridStackCellHeight: number,
  widgetFrameLookup: widget.Frame.Lookup,
): ents.core.mosaic.Page.Content.WithoutSegments {
  return {
    root: widget.Frame.toRootElement(
      segment.widgetFrames,
      gridStackCellHeight,
      widgetFrameLookup,
    ),
  }
}

/**
 * Converts {@link Segment[]} into
 * {@link ents.core.mosaic.Page.Content.WithSegments}
 */
function _buildWithSegmentsContent(
  segments: readonly Segment[],
  gridStackCellHeight: number,
  widgetFrameLookup: widget.Frame.Lookup,
): ents.core.mosaic.Page.Content.WithSegments {
  return {
    navigator: ents.core.mosaic.Page.Segment.Navigator.Tabs,
    segments: segments.map(segment => {
      const root = widget.Frame.toRootElement(
        segment.widgetFrames,
        gridStackCellHeight,
        widgetFrameLookup,
      )
      return {
        ...segment,
        root,
      }
    }),
  }
}
